import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import PanKycAdminReport from './PanKycAdminReport';
import PanKycUserCard from '../../do-finance-apply/components/PanKycUserCard';
import GstinKycAdminReport from './GstinKycAdminReport';
import GstinKycUserReport from '../../do-finance-apply/components/GstinKycUserReport';
import CinKycAdminReport from './CinKycAdminReport';
import CinKycUserReport from '../../do-finance-apply/components/CinKycUserReport';
import SocialKycAdminReport from './SocialKycAdminReport';
import SocialKycUserReport from '../../do-finance-apply/components/SocialKycUserReport';
import EquifaxAdminReport from './EquifaxAdminReport';
import EquifaxUserReport from '../../do-finance-apply/components/EquifaxUserReport';
import OmniscoreAdminReport from './OmniscoreAdminReport';
import OmniscoreUserReport from '../../do-finance-apply/components/OmniscoreUserReport';

// Maps API Lab keys -> the Admin/User report pair that can render that
// vendor's response. Not every API Lab entry has a report built yet — only
// keys present here show the "View Admin/User Report" buttons.
// To add a new one later: import the two components above and add one line here.
const REPORT_CONFIGS = {
  pan_decentro:   { label: 'PAN Plus',              Admin: PanKycAdminReport,    User: PanKycUserCard },
  gstin_detailed: { label: 'GSTIN Detailed',         Admin: GstinKycAdminReport,  User: GstinKycUserReport },
  cin_decentro:   { label: 'CIN Verification',       Admin: CinKycAdminReport,    User: CinKycUserReport },
  social_intel:   { label: 'Social Intelligence',    Admin: SocialKycAdminReport, User: SocialKycUserReport },
  equifax:        { label: 'Equifax Credit Report',  Admin: EquifaxAdminReport,   User: EquifaxUserReport },
  omniscore:      { label: 'OmniScore',              Admin: OmniscoreAdminReport, User: OmniscoreUserReport },
};

const API_CONFIGS = {
  pan:                { label: 'PAN Verification',    vendor: 'Verifico',  color: '#1e4a72', bg: '#dbeafe', fields: [{ key: 'pan_number', label: 'PAN Number', placeholder: 'ABCDE1234F', upper: true, alnum: true, maxLength: 10, pattern: '^[A-Z]{5}[0-9]{4}[A-Z]$', formatHint: 'Format: AAAAA9999A (10 characters)' }] },
  equifax:            { label: 'Credit Report (Full & Summary)', vendor: 'Decentro',  color: '#92400e', bg: '#fef3c7', fields: [{ key: 'pan', label: 'PAN', placeholder: 'ABCDE1234F', upper: true, alnum: true, maxLength: 10, pattern: '^[A-Z]{5}[0-9]{4}[A-Z]$', formatHint: 'Format: AAAAA9999A (10 characters)' }, { key: 'name', label: 'Full Name', placeholder: 'As on PAN', maxLength: 100 }, { key: 'mobile', label: 'Mobile (10 digit)', placeholder: '9876543210', numeric: true, maxLength: 10, pattern: '^[0-9]{10}$', formatHint: '10 digit number' }, { key: 'dob', label: 'DOB (DD-MM-YYYY)', placeholder: '01-01-1985', optional: true, dateLike: true, maxLength: 10, pattern: '^\\d{2}-\\d{2}-\\d{4}$', formatHint: 'Format: DD-MM-YYYY' }, { key: 'pincode', label: 'Pincode', placeholder: '400001', optional: true, numeric: true, maxLength: 6, pattern: '^[0-9]{6}$', formatHint: '6 digit pincode' }, { key: 'address', label: 'Address', placeholder: 'House/Street, Area', optional: true, maxLength: 200 }] },
  social_intel:       { label: 'Social Intelligence',  vendor: 'Decentro',  color: '#92400e', bg: '#fef3c7', fields: [{ key: 'mobile', label: 'Mobile (10 digit)', placeholder: '9876543210', numeric: true, maxLength: 10, pattern: '^[0-9]{10}$', formatHint: '10 digit number' }] },
  omniscore:          { label: 'Omniscore Advance',    vendor: 'Decentro',  color: '#92400e', bg: '#fef3c7', fields: [{ key: 'mobile', label: 'Mobile (10 digit)', placeholder: '9876543210', numeric: true, maxLength: 10, pattern: '^[0-9]{10}$', formatHint: '10 digit number' }, { key: 'name', label: 'Full Name', placeholder: 'As on PAN', maxLength: 100 }, { key: 'pan', label: 'PAN', placeholder: 'ABCDE1234F', upper: true, alnum: true, maxLength: 10, pattern: '^[A-Z]{5}[0-9]{4}[A-Z]$', formatHint: 'Format: AAAAA9999A (10 characters)' }] },
  pan_decentro:       { label: 'PAN Detailed',         vendor: 'Decentro', color: '#92400e', bg: '#fef3c7', requiresConsent: true, fields: [{ key: 'id_number', label: 'PAN Number', placeholder: 'ABCDE1234F', upper: true, alnum: true, maxLength: 10, pattern: '^[A-Z]{5}[0-9]{4}[A-Z]$', formatHint: 'Format: AAAAA9999A (10 characters)' }] },
  gstin_detailed:     { label: 'GST Filing Verification', vendor: 'Decentro', color: '#92400e', bg: '#fef3c7', requiresConsent: true, consentLabel: 'I confirm consent & purpose have been obtained from the GSTIN holder before fetching these details.', fields: [{ key: 'id_number', label: 'GSTIN', placeholder: '29ABCDE1234F1Z5', upper: true, alnum: true, maxLength: 15, pattern: '^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$', formatHint: '15-character GSTIN' }] },
  cin_decentro:       { label: 'CIN Verification', vendor: 'Decentro', color: '#92400e', bg: '#fef3c7', requiresConsent: true, consentLabel: 'I confirm consent & purpose have been obtained before fetching this CIN data.', fields: [{ key: 'id_number', label: 'CIN', placeholder: 'U12345MH2020PTC123456', upper: true, alnum: true, maxLength: 21, pattern: '^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$', formatHint: '21-character CIN' }] },
  gstin_basic:        { label: 'GSTIN Basic', vendor: 'Decentro', color: '#92400e', bg: '#fef3c7', requiresConsent: true, consentLabel: 'I confirm consent & purpose have been obtained from the GSTIN holder before fetching these details.', fields: [{ key: 'id_number', label: 'GSTIN', placeholder: '29ABCDE1234F1Z5', upper: true, alnum: true, maxLength: 15, pattern: '^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$', formatHint: '15-character GSTIN' }] },
  pan_basic:          { label: 'PAN Basic', vendor: 'Decentro', color: '#92400e', bg: '#fef3c7', requiresConsent: true, consentLabel: 'I confirm consent & purpose have been obtained from the PAN holder before fetching these details.', fields: [{ key: 'id_number', label: 'PAN Number', placeholder: 'ABCDE1234F', upper: true, alnum: true, maxLength: 10, pattern: '^[A-Z]{5}[0-9]{4}[A-Z]$', formatHint: 'Format: AAAAA9999A (10 characters)' }] },
};

const ApiLabTab = () => {
  const [selectedApi, setSelectedApi] = useState(() => localStorage.getItem('adminLabApi') || 'pan');
  const [inputs, setInputs] = useState(() => { try { return JSON.parse(localStorage.getItem('adminLabInputs') || '{}'); } catch { return {}; } });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(() => { try { return JSON.parse(localStorage.getItem('adminLabResult') || 'null'); } catch { return null; } });
  const [copied, setCopied] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [activeReport, setActiveReport] = useState(null); // { config, response, view: 'admin'|'user' }

  // Persist state across navigation
  useEffect(() => { localStorage.setItem('adminLabApi', selectedApi); }, [selectedApi]);
  useEffect(() => { localStorage.setItem('adminLabInputs', JSON.stringify(inputs)); }, [inputs]);
  useEffect(() => { localStorage.setItem('adminLabResult', JSON.stringify(result)); }, [result]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await supabase
        .from('admin_api_lab_logs')
        .select('*')
        .order('called_at', { ascending: false })
        .limit(50);
      setHistory(data ?? []);
    } catch (e) {
      console.error('History fetch error:', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { if (showHistory) fetchHistory(); }, [showHistory]);

  const config = API_CONFIGS[selectedApi] ?? API_CONFIGS.pan;
  const reportConfig = REPORT_CONFIGS[selectedApi] ?? null;
  const hasInvalidField = config.fields.some(f => {
    const v = (inputs[f.key] ?? '').trim();
    return !!(v && f.pattern && !(new RegExp(f.pattern)).test(v));
  });

  const handleApiChange = (api) => { setSelectedApi(api); setInputs({}); setResult(null); setConsentChecked(false); setActiveReport(null); };

  const handleRun = async () => {
    setLoading(true); setResult(null); setActiveReport(null);
    try {
      const { data, error } = await supabase.functions.invoke('admin-api-lab', { body: { api: selectedApi, payload: inputs } });
      if (error) throw error;
      setResult(data);
    } catch (err) {
      setResult({ error: err.message ?? 'Call failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(result?.response ?? result, null, 2));
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  // Repopulate the input panel from a past call so it's ready to re-submit.
  const handleRecheck = (log) => {
    setSelectedApi(log.api);
    setInputs(log.payload ?? {});
    setResult(null);
    setConsentChecked(false);
    setActiveReport(null);
    setExpandedLog(null);
    setShowHistory(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold" style={{ color: '#1f2937' }}>API Lab</h2>
        <p className="text-sm" style={{ color: '#6b7280' }}>Test vendor APIs directly. Admin only. No user binding.</p>
      </div>

      {/* API selector */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(API_CONFIGS).map(([key, cfg]) => (
          <button key={key} onClick={() => handleApiChange(key)}
            className="px-3 py-1.5 rounded-xl text-xs font-semibold border transition"
            style={{ backgroundColor: selectedApi === key ? cfg.color : '#fff', color: selectedApi === key ? '#fff' : '#374151', borderColor: selectedApi === key ? cfg.color : '#e5e7eb' }}
          >
            {cfg.label}
          </button>
        ))}
      </div>

      {/* Input panel */}
      <div className="bg-white rounded-2xl border shadow-sm p-5" style={{ borderColor: '#e5e7eb' }}>
        <div className="flex items-center gap-3 mb-4">
          <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>{config.label}</h3>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: config.bg, color: config.color }}>{config.vendor}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {config.fields.map(field => {
            const val = inputs[field.key] ?? '';
            const trimmed = val.trim();
            const invalid = !!(trimmed && field.pattern && !(new RegExp(field.pattern)).test(trimmed));
            return (
              <div key={field.key}>
                <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                  {field.label}{field.optional && <span style={{ color: '#9ca3af' }}> (optional)</span>}
                </label>
                <input type="text" value={val} maxLength={field.maxLength || undefined}
                  inputMode={field.numeric ? 'numeric' : undefined}
                  onChange={e => {
                    let next = e.target.value;
                    if (field.numeric) next = next.replace(/[^0-9]/g, '');
                    else if (field.alnum) next = next.replace(/[^A-Za-z0-9]/g, '');
                    else if (field.dateLike) next = next.replace(/[^0-9-]/g, '');
                    if (field.upper) next = next.toUpperCase();
                    if (field.maxLength) next = next.slice(0, field.maxLength);
                    setInputs(prev => ({ ...prev, [field.key]: next }));
                  }}
                  placeholder={field.placeholder}
                  className="w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  style={{ borderColor: invalid ? '#dc2626' : '#d1d5db', color: '#1f2937' }}
                />
                {invalid && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>{field.formatHint || 'Invalid format'}</p>}
              </div>
            );
          })}
        </div>
        {config.requiresConsent && (
          <label className="flex items-start gap-2 mb-4 cursor-pointer">
            <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)}
              className="mt-0.5" style={{ width: 15, height: 15 }} />
            <span className="text-xs" style={{ color: '#374151' }}>
              {config.consentLabel || 'I confirm consent & purpose have been obtained from the PAN holder before fetching these details.'}
            </span>
          </label>
        )}
        <button onClick={handleRun} disabled={loading || hasInvalidField || (config.requiresConsent && !consentChecked)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: config.color }}
        >
          {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Zap" size={15} />}
          {loading ? 'Running...' : 'Run'}
        </button>
      </div>

      {/* History toggle */}
      <div className="flex justify-end">
        <button onClick={() => setShowHistory(h => !h)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition"
          style={{ borderColor: '#e5e7eb', color: showHistory ? '#1e4a72' : '#374151', backgroundColor: showHistory ? '#dbeafe' : '#fff' }}>
          <Icon name="History" size={13} />
          {showHistory ? 'Hide History' : 'Call History'}
        </button>
      </div>

      {/* History panel */}
      {showHistory && (
        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#f3f4f6', backgroundColor: '#fffbf5' }}>
            <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>Last 50 API calls</h3>
            <button onClick={fetchHistory} disabled={historyLoading}
              className="text-xs px-2 py-1 rounded-lg border" style={{ borderColor: '#e5e7eb', color: '#374151' }}>
              {historyLoading ? '...' : 'Refresh'}
            </button>
          </div>
          {history.length === 0
            ? <div className="text-center py-8 text-sm" style={{ color: '#6b7280' }}>{historyLoading ? 'Loading...' : 'No calls yet'}</div>
            : (
              <div className="divide-y" style={{ borderColor: '#f9fafb' }}>
                {history.map(log => {
                  const logReportConfig = REPORT_CONFIGS[log.api] ?? null;
                  return (
                  <div key={log.id}>
                    <button onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: log.success ? '#22c55e' : '#ef4444' }} />
                        <span className="text-xs font-semibold uppercase" style={{ color: '#1f2937' }}>{log.api}</span>
                        <span className="text-xs" style={{ color: '#6b7280' }}>
                          {Object.entries(log.payload ?? {}).map(([k, v]) => `${v}`).join(' · ').slice(0, 30)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs" style={{ color: '#9ca3af' }}>{log.latency_ms}ms</span>
                        <span className="text-xs" style={{ color: '#9ca3af' }}>
                          {new Date(log.called_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <Icon name={expandedLog === log.id ? 'ChevronUp' : 'ChevronDown'} size={12} />
                      </div>
                    </button>
                    {expandedLog === log.id && (
                      <div className="px-4 pb-3">
                        <div className="flex gap-2 mb-2">
                          <button onClick={() => handleRecheck(log)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition hover:bg-gray-50"
                            style={{ borderColor: '#e5e7eb', color: '#374151' }}
                          >
                            <Icon name="RotateCcw" size={13} />
                            Recheck
                          </button>
                        </div>
                        {logReportConfig && log.success && (
                          <div className="flex gap-2 mb-2">
                            <button onClick={() => setActiveReport({ config: logReportConfig, response: log.response, view: 'admin' })}
                              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white transition hover:opacity-90"
                              style={{ backgroundColor: API_CONFIGS[log.api]?.color ?? '#92400e' }}
                            >
                              <Icon name="FileText" size={13} />
                              View Admin Report
                            </button>
                            <button onClick={() => setActiveReport({ config: logReportConfig, response: log.response, view: 'user' })}
                              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition hover:bg-gray-50"
                              style={{ borderColor: API_CONFIGS[log.api]?.color ?? '#92400e', color: API_CONFIGS[log.api]?.color ?? '#92400e' }}
                            >
                              <Icon name="User" size={13} />
                              View User Report
                            </button>
                          </div>
                        )}
                        <pre className="text-xs rounded-xl p-3 overflow-auto"
                          style={{ backgroundColor: '#0f172a', color: '#e2e8f0', maxHeight: '300px', fontFamily: 'monospace', lineHeight: 1.5 }}>
                          {JSON.stringify(log.response, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )
          }
        </div>
      )}

      {/* Response */}
      {result && (
        <div className="bg-white rounded-2xl border shadow-sm p-5" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>Response</h3>
              {result.latency_ms && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#d1fae5', color: '#065f46' }}>{result.latency_ms}ms</span>
              )}
            </div>
            <button onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition hover:bg-gray-50"
              style={{ borderColor: '#e5e7eb', color: '#374151' }}
            >
              <Icon name={copied ? 'Check' : 'Copy'} size={12} />
              {copied ? 'Copied!' : 'Copy JSON'}
            </button>
          </div>

          {reportConfig && !result.error && (
            <div className="flex gap-2 mb-3">
              <button onClick={() => setActiveReport({ config: reportConfig, response: result?.response, view: 'admin' })}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white transition hover:opacity-90"
                style={{ backgroundColor: config.color }}
              >
                <Icon name="FileText" size={13} />
                View Admin Report
              </button>
              <button onClick={() => setActiveReport({ config: reportConfig, response: result?.response, view: 'user' })}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition hover:bg-gray-50"
                style={{ borderColor: config.color, color: config.color }}
              >
                <Icon name="User" size={13} />
                View User Report
              </button>
            </div>
          )}

          <pre className="text-xs rounded-xl p-4 overflow-auto"
            style={{ backgroundColor: '#0f172a', color: '#e2e8f0', maxHeight: '500px', fontFamily: 'monospace', lineHeight: 1.6 }}
          >
            {JSON.stringify(result?.response ?? result, null, 2)}
          </pre>
        </div>
      )}

      {/* Report viewer modal — hosts the real Admin/User component, which
          already has its own Share + Download-as-PDF buttons built in.
          Works identically whether opened from the live Response panel or
          from a past Call History entry — both just hand it a config +
          response and let it render, so a paid-for historic result never
          has to be re-fetched just to view or PDF it again. */}
      {activeReport && (
        <div
          className="fixed inset-0 z-50 flex flex-col"
          style={{ backgroundColor: 'rgba(15,23,42,0.6)' }}
          onClick={() => setActiveReport(null)}
        >
          <div
            className="bg-white mx-auto my-4 rounded-2xl overflow-hidden flex flex-col"
            style={{ width: '100%', maxWidth: 640, maxHeight: '92vh' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0" style={{ borderColor: '#f3f4f6' }}>
              <span className="text-sm font-bold" style={{ color: '#1f2937' }}>
                {activeReport.config.label} · {activeReport.view === 'admin' ? 'Admin' : 'User'} view
              </span>
              <button onClick={() => setActiveReport(null)} className="p-1 rounded-lg hover:bg-gray-100">
                <Icon name="X" size={18} color="#6b7280" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              {activeReport.view === 'admin'
                ? <activeReport.config.Admin data={activeReport.response} />
                : <activeReport.config.User data={activeReport.response} />
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiLabTab;

